/**
 * Scheduled meetings in the Inbox (TRUS-691 phase 1).
 *
 * Typed client for /api/messaging/meetings. The backend owns the contract
 * (meetings_contract.md): a meeting is a scheduled call with attendees, an
 * ICS invite and a card message in its conversation; joining it lazily starts
 * a normal Inbox call (calls_contract*.md) that carries `meeting_id`.
 *
 * Two transports, like callsApi:
 *  - `meetingsApi`        staff — the axios `apiClient` (auth + X-CBO-ID)
 *  - `memberMeetingsApi`  member portal — `memberApiFetch` (member token).
 *    Members can view the meetings they are invited to, RSVP and join; they
 *    never schedule (403 members_cannot_schedule), and the member client
 *    refuses the staff-only calls without a request.
 *
 * Every field the contract marks as later / optional is parsed defensively:
 * an older server omits it and absent means "off".
 */

import { apiClient } from './apiClient';
import { memberApiFetch } from '@/lib/memberAuth';
import { CallsApiError, parseCallTime, type Call, type CallJoin, type CallMode, type CallRecordingConsent } from './callsApi';

export const MEETINGS_BASE = '/api/messaging/meetings';

export type MeetingStatus = 'scheduled' | 'live' | 'ended' | 'cancelled';
export type MeetingRsvp = 'pending' | 'accepted' | 'declined' | 'tentative';
export type MeetingAttendeeKind = 'staff' | 'member' | 'external';
export type MeetingLinkType = 'private' | 'guest';

/** Duration bounds (contract: 5–480 minutes). */
export const MEETING_MIN_DURATION = 5;
export const MEETING_MAX_DURATION = 480;
/** Attendee caps (contract). */
export const MEETING_MAX_EXTERNAL = 25;
export const MEETING_MAX_ATTENDEES = 100;

export interface MeetingAttendee {
  kind: MeetingAttendeeKind;
  user_id: number | null;
  member_id: number | null;
  /** Masked for non-hosts (e.g. "j***@example.org"); null for staff / members without one. */
  email: string | null;
  name: string;
  rsvp: MeetingRsvp;
}

export interface Meeting {
  id: number;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  duration_min: number;
  mode: CallMode;
  status: MeetingStatus;
  link_type: MeetingLinkType;
  host: { user_id: number | null; name: string };
  channel_id: number | null;
  channel_name: string | null;
  attendees: MeetingAttendee[];
  join_url_staff: string | null;
  join_url_member: string | null;
  /** Phase 2: the org allows guest links for this meeting. */
  guest_link_enabled: boolean;
  can_edit: boolean;
  can_join_now: boolean;
  /** When Join opens (10 min before for attendees; the host may start earlier). */
  joinable_from: string | null;
  call_id: number | null;
  calendar_event_id: number | null;
  /** Per-caller Google / Outlook "add to calendar" template links. */
  add_to_calendar: { google: string | null; outlook: string | null };
  /** The caller's own RSVP, when the server says (else matched from attendees). */
  my_rsvp: MeetingRsvp | null;
  /** The host ticked "Use this title in invitations" (`use_title_in_invites`). */
  title_in_invitations: boolean;
  /** The org's time zone (IANA), when the server says. */
  org_timezone: string | null;
  /** TRUS-697: the meeting's own zone (IANA) - the host's unless they picked another. */
  timezone: string | null;
  /** A signed, no-auth ICS download link ("Download .ics"). */
  ics_url: string | null;
  /** The caller hosts this meeting. */
  i_am_host: boolean;
  /** Phase 2: a guest link is active (when the server says), and until when. */
  guest_link_active: boolean;
  guest_link_expires_at: string | null;
}

export interface MeetingAttendeesInput {
  user_ids: number[];
  member_ids: number[];
  external: { email: string; name: string }[];
}

export interface MeetingNotify {
  push: boolean;
  email: boolean;
  sms: boolean;
  /** TRUS-694: also add the invitees to the host's own Outlook / Google copy of the meeting. */
  calendar_attendees?: boolean;
}

export interface CreateMeetingInput {
  title: string;
  starts_at: string;
  duration_min: number;
  mode: CallMode;
  channel_id?: number | null;
  attendees: MeetingAttendeesInput;
  description?: string | null;
  notify: MeetingNotify;
  /** Show the typed title in emails / ICS to members and externals (default: the generic title). */
  title_in_invitations?: boolean;
  /** TRUS-697: the meeting's own IANA zone (the dialog's picker; default the host's). */
  timezone?: string;
}

export type UpdateMeetingInput = Partial<
  Pick<
    CreateMeetingInput,
    'title' | 'starts_at' | 'duration_min' | 'mode' | 'attendees' | 'description' | 'title_in_invitations' | 'timezone'
  >
>;

export interface ListMeetingsParams {
  from?: string;
  to?: string;
  channelId?: number | null;
  mine?: boolean;
}

/** `metadata.meeting` on the meeting card message. */
export interface MeetingMessageMeta {
  meeting_id: number;
  title: string | null;
  starts_at: string | null;
  duration_min: number | null;
  mode: CallMode | null;
  status: MeetingStatus | null;
  /** TRUS-697: the meeting's own zone, when the card carries it. */
  timezone?: string | null;
}

export interface MeetingsTransport {
  request<T>(method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, opts?: { params?: Record<string, string | number>; body?: unknown }): Promise<T>;
  /** GET a file (the ICS). */
  blob(path: string): Promise<Blob>;
}

/** An attendee the server did not invite (create / PATCH `rejected`). */
export interface MeetingRejectedAttendee {
  kind: 'staff' | 'member' | 'external';
  id: number | null;
  email: string | null;
  reason: string;
}

/** POST / PATCH result: the meeting plus anyone who was not invited. */
export interface MeetingSaveResult {
  meeting: Meeting;
  rejected: MeetingRejectedAttendee[];
}

/** GET /config */
export interface MeetingsConfig {
  enabled: boolean;
  can_schedule: boolean;
}

export const DISABLED_MEETINGS_CONFIG: MeetingsConfig = { enabled: false, can_schedule: false };

export interface MeetingsApi {
  kind: 'staff' | 'member';
  /** Never throws: an error / older server means "meetings off". */
  getConfig(): Promise<MeetingsConfig>;
  create(input: CreateMeetingInput): Promise<MeetingSaveResult>;
  list(params?: ListMeetingsParams): Promise<Meeting[]>;
  get(id: number): Promise<Meeting>;
  update(id: number, input: UpdateMeetingInput): Promise<MeetingSaveResult>;
  cancel(id: number): Promise<Meeting>;
  rsvp(id: number, rsvp: MeetingRsvp): Promise<Meeting | null>;
  ics(id: number): Promise<Blob>;
  /** Starts (lazily) or joins the meeting's call. */
  join(id: number): Promise<CallJoin>;
  /** Phase 2 (host): issue a guest link — re-issuing revokes the previous one. */
  createGuestLink(id: number): Promise<GuestLink>;
  /** Phase 2 (host): revoke the guest link. */
  revokeGuestLink(id: number): Promise<void>;
  /** Phase 2 (staff in the call): the meeting's guests (waiting first). */
  lobby(id: number): Promise<MeetingGuest[]>;
  admitGuest(id: number, guestId: number): Promise<void>;
  denyGuest(id: number, guestId: number): Promise<void>;
  removeGuest(id: number, guestId: number): Promise<void>;
  /** Phase 2 (org admin): Settings → Meetings. */
  getOrgSettings(): Promise<MeetingsOrgSettingsResponse>;
  saveOrgSettings(settings: MeetingsOrgSettings): Promise<MeetingsOrgSettingsResponse>;
}

/** POST /{id}/guest-link */
export interface GuestLink {
  url: string;
  expires_at: string | null;
}

export type MeetingGuestStatus = 'waiting' | 'admitted' | 'denied' | 'removed' | 'left';

export interface MeetingGuest {
  id: number;
  display_name: string;
  status: MeetingGuestStatus;
  created_at: string | null;
  /** TRUS-693: an admitted guest's answer to the recording question (null when unknown / waiting). */
  recording?: CallRecordingConsent | null;
}

/** `cbo.settings.meetings` (contract phase 2). */
export interface MeetingsOrgSettings {
  guest_links_enabled: boolean;
  guest_link_max_hours: number;
  max_guests: number;
}

export interface MeetingsOrgSettingsResponse {
  settings: MeetingsOrgSettings;
  /** The org has the guest-links capability (else the settings do nothing). */
  guest_links_available: boolean;
}

export const GUEST_LINK_MAX_HOURS_LIMIT = 168;
export const MAX_GUESTS_LIMIT = 25;
export const DEFAULT_MEETINGS_ORG_SETTINGS: MeetingsOrgSettings = {
  guest_links_enabled: false,
  guest_link_max_hours: 24,
  max_guests: 10,
};

const GUEST_STATUSES = new Set<MeetingGuestStatus>(['waiting', 'admitted', 'denied', 'removed', 'left']);

export function parseMeetingGuest(raw: unknown): MeetingGuest | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, any>;
  const id = idOrNull(r.id ?? r.guest_id);
  if (!id) return null;
  return {
    id,
    display_name: str(r.display_name) || str(r.name) || '',
    status: GUEST_STATUSES.has(r.status) ? r.status : 'waiting',
    created_at: str(r.created_at),
    recording: r.recording === 'consented' || r.recording === 'declined' || r.recording === 'pending' ? r.recording : null,
  };
}

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

export function parseOrgSettings(raw: any): MeetingsOrgSettingsResponse {
  const s = raw?.settings ?? raw?.meetings ?? raw ?? {};
  return {
    settings: {
      guest_links_enabled: s.guest_links_enabled === true,
      guest_link_max_hours: clampInt(s.guest_link_max_hours, 1, GUEST_LINK_MAX_HOURS_LIMIT, 24),
      max_guests: clampInt(s.max_guests, 1, MAX_GUESTS_LIMIT, 10),
    },
    guest_links_available: s.guest_links_available === true,
  };
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const STATUSES = new Set<MeetingStatus>(['scheduled', 'live', 'ended', 'cancelled']);
const RSVPS = new Set<MeetingRsvp>(['pending', 'accepted', 'declined', 'tentative']);

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}
function idOrNull(v: unknown): number | null {
  const n = Number(v);
  return v != null && Number.isFinite(n) && n > 0 ? n : null;
}

export function parseAttendee(raw: unknown): MeetingAttendee | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, any>;
  const user_id = idOrNull(r.user_id);
  const member_id = idOrNull(r.member_id);
  const kind: MeetingAttendeeKind =
    r.kind === 'staff' || r.kind === 'member' || r.kind === 'external'
      ? r.kind
      : user_id
        ? 'staff'
        : member_id
          ? 'member'
          : 'external';
  return {
    kind,
    user_id,
    member_id,
    email: str(r.email),
    name: str(r.name) || str(r.email) || '',
    rsvp: RSVPS.has(r.rsvp) ? r.rsvp : 'pending',
  };
}

/** A meeting from the server, or null when it is unusable (no id / start). */
export function parseMeeting(raw: unknown): Meeting | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, any>;
  const id = idOrNull(r.id);
  const startsAt = str(r.starts_at);
  if (!id || !startsAt) return null;
  const duration = Number(r.duration_min);
  const durationMin = Number.isFinite(duration) && duration > 0 ? Math.round(duration) : 30;
  const start = parseCallTime(startsAt);
  const endsAt =
    str(r.ends_at) || (start != null ? new Date(start + durationMin * 60_000).toISOString() : startsAt);
  const host = r.host && typeof r.host === 'object' ? r.host : {};
  const atc = r.add_to_calendar && typeof r.add_to_calendar === 'object' ? r.add_to_calendar : {};
  return {
    id,
    title: str(r.title) || '',
    description: str(r.description),
    starts_at: startsAt,
    ends_at: endsAt,
    duration_min: durationMin,
    mode: r.mode === 'audio' ? 'audio' : 'video',
    status: STATUSES.has(r.status) ? r.status : 'scheduled',
    link_type: r.link_type === 'guest' ? 'guest' : 'private',
    host: { user_id: idOrNull(host.user_id), name: str(host.name) || '' },
    channel_id: idOrNull(r.channel_id),
    channel_name: str(r.channel_name),
    attendees: Array.isArray(r.attendees)
      ? r.attendees.map(parseAttendee).filter((a: MeetingAttendee | null): a is MeetingAttendee => a != null)
      : [],
    join_url_staff: str(r.join_url_staff),
    join_url_member: str(r.join_url_member),
    guest_link_enabled: r.guest_link_enabled === true,
    can_edit: r.can_edit === true,
    can_join_now: r.can_join_now === true,
    joinable_from: str(r.joinable_from),
    call_id: idOrNull(r.call_id),
    calendar_event_id: idOrNull(r.calendar_event_id),
    add_to_calendar: { google: str(atc.google), outlook: str(atc.outlook) },
    my_rsvp: RSVPS.has(r.my_rsvp) ? r.my_rsvp : null,
    title_in_invitations: r.use_title_in_invites === true || r.title_in_invitations === true,
    org_timezone: str(r.org_timezone),
    timezone: str(r.timezone),
    ics_url: str(r.ics_url),
    i_am_host: r.i_am_host === true,
    // `guest_link: {active, expires_at} | null` (host / org admin only).
    guest_link_active: r.guest_link?.active === true || r.guest_link_active === true,
    guest_link_expires_at: str(r.guest_link?.expires_at) || str(r.guest_link_expires_at),
  };
}

function saveResultFrom(res: any): MeetingSaveResult {
  const rejected = Array.isArray(res?.rejected)
    ? res.rejected
        .filter((r: any) => r && typeof r === 'object')
        .map((r: any) => ({
          kind: r.kind === 'member' || r.kind === 'external' ? r.kind : 'staff',
          id: idOrNull(r.id),
          email: str(r.email),
          reason: str(r.reason) || 'rejected',
        }))
    : [];
  return { meeting: meetingFrom(res), rejected };
}

/** The body field names the server takes (title_in_invitations → use_title_in_invites). */
function toBody(input: Record<string, any>): Record<string, unknown> {
  const { title_in_invitations, ...rest } = input;
  const body: Record<string, unknown> = { ...rest };
  if (title_in_invitations !== undefined) body.use_title_in_invites = !!title_in_invitations;
  return body;
}

function meetingFrom(res: any): Meeting {
  const m = parseMeeting(res?.meeting ?? res);
  if (!m) throw new CallsApiError('Unexpected response', 0, 'bad_response');
  return m;
}

/** The meeting card metadata on a message, if any. */
export function meetingMetaOf(metadata: Record<string, any> | null | undefined): MeetingMessageMeta | null {
  const m = metadata?.meeting;
  if (!m || typeof m !== 'object') return null;
  const id = idOrNull(m.meeting_id ?? m.id);
  if (!id) return null;
  return {
    meeting_id: id,
    title: str(m.title),
    starts_at: str(m.starts_at),
    duration_min: Number.isFinite(Number(m.duration_min)) && Number(m.duration_min) > 0 ? Math.round(Number(m.duration_min)) : null,
    mode: m.mode === 'audio' || m.mode === 'video' ? m.mode : null,
    status: STATUSES.has(m.status) ? m.status : null,
    ...(str(m.timezone) ? { timezone: str(m.timezone) } : {}),
  };
}

/** The call join payload (`room` per the meetings contract, `room_name` per calls). */
export function parseMeetingJoin(raw: any, fallbackMode: CallMode): CallJoin {
  const call = (raw?.call ?? null) as Call | null;
  const token = str(raw?.token);
  const room = str(raw?.room_name) || str(raw?.room) || call?.room_name || null;
  if (!call || !token || !room) throw new CallsApiError('Unexpected join response', 0, 'bad_response');
  const expires = Number(raw?.expires_in);
  return {
    call,
    token,
    identity: str(raw?.identity) || '',
    room_name: room,
    mode: raw?.mode === 'audio' || raw?.mode === 'video' ? raw.mode : call.mode || fallbackMode,
    expires_in: Number.isFinite(expires) && expires > 0 ? expires : 3600,
  };
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

function staffOnly(kind: 'staff' | 'member', what: string) {
  if (kind === 'member') throw new CallsApiError(`Members cannot ${what}`, 403, 'members_cannot_schedule');
}

export function createMeetingsApi(transport: MeetingsTransport, kind: 'staff' | 'member'): MeetingsApi {
  return {
    kind,
    async getConfig() {
      try {
        const r = await transport.request<any>('GET', `${MEETINGS_BASE}/config`);
        const enabled = r?.enabled === true;
        return { enabled, can_schedule: enabled && kind === 'staff' && r?.can_schedule === true };
      } catch {
        return DISABLED_MEETINGS_CONFIG;
      }
    },
    async create(input) {
      staffOnly(kind, 'schedule meetings');
      const body = toBody({
        title: input.title,
        starts_at: input.starts_at,
        duration_min: input.duration_min,
        mode: input.mode,
        attendees: input.attendees,
        notify: input.notify,
        title_in_invitations: !!input.title_in_invitations,
      });
      if (input.channel_id != null) body.channel_id = input.channel_id;
      if (input.timezone) body.timezone = input.timezone;
      if (input.description) body.description = input.description;
      return saveResultFrom(await transport.request('POST', `${MEETINGS_BASE}`, { body }));
    },
    async list(params = {}) {
      const query: Record<string, string | number> = {};
      if (params.from) query.from = params.from;
      if (params.to) query.to = params.to;
      if (params.channelId != null) query.channel_id = params.channelId;
      if (params.mine) query.mine = 'true';
      const res = await transport.request<any>('GET', `${MEETINGS_BASE}`, {
        params: Object.keys(query).length ? query : undefined,
      });
      const list = Array.isArray(res?.meetings) ? res.meetings : Array.isArray(res) ? res : [];
      return list.map(parseMeeting).filter((m: Meeting | null): m is Meeting => m != null);
    },
    async get(id) {
      return meetingFrom(await transport.request('GET', `${MEETINGS_BASE}/${id}`));
    },
    async update(id, input) {
      staffOnly(kind, 'edit meetings');
      return saveResultFrom(await transport.request('PATCH', `${MEETINGS_BASE}/${id}`, { body: toBody(input) }));
    },
    async cancel(id) {
      staffOnly(kind, 'cancel meetings');
      return meetingFrom(await transport.request('POST', `${MEETINGS_BASE}/${id}/cancel`));
    },
    async rsvp(id, rsvp) {
      const res = await transport.request<any>('POST', `${MEETINGS_BASE}/${id}/rsvp`, { body: { rsvp } });
      return parseMeeting(res?.meeting ?? res);
    },
    ics: (id) => transport.blob(`${MEETINGS_BASE}/${id}/ics`),
    async join(id) {
      const res = await transport.request<any>('POST', `${MEETINGS_BASE}/${id}/join`);
      return parseMeetingJoin(res, 'video');
    },
    async createGuestLink(id) {
      staffOnly(kind, 'create guest links');
      const res = await transport.request<any>('POST', `${MEETINGS_BASE}/${id}/guest-link`);
      const url = str(res?.url);
      if (!url) throw new CallsApiError('Unexpected response', 0, 'bad_response');
      return { url, expires_at: str(res?.expires_at) };
    },
    async revokeGuestLink(id) {
      staffOnly(kind, 'revoke guest links');
      await transport.request('DELETE', `${MEETINGS_BASE}/${id}/guest-link`);
    },
    async lobby(id) {
      staffOnly(kind, 'see the lobby');
      const res = await transport.request<any>('GET', `${MEETINGS_BASE}/${id}/lobby`);
      const list = Array.isArray(res?.guests) ? res.guests : Array.isArray(res) ? res : [];
      return list.map(parseMeetingGuest).filter((g: MeetingGuest | null): g is MeetingGuest => g != null);
    },
    async admitGuest(id, guestId) {
      staffOnly(kind, 'admit guests');
      await transport.request('POST', `${MEETINGS_BASE}/${id}/lobby/${guestId}/admit`);
    },
    async denyGuest(id, guestId) {
      staffOnly(kind, 'deny guests');
      await transport.request('POST', `${MEETINGS_BASE}/${id}/lobby/${guestId}/deny`);
    },
    async removeGuest(id, guestId) {
      staffOnly(kind, 'remove guests');
      await transport.request('POST', `${MEETINGS_BASE}/${id}/guests/${guestId}/remove`);
    },
    async getOrgSettings() {
      staffOnly(kind, 'read meeting settings');
      return parseOrgSettings(await transport.request<any>('GET', `${MEETINGS_BASE}/settings`));
    },
    async saveOrgSettings(settings) {
      staffOnly(kind, 'change meeting settings');
      return parseOrgSettings(
        await transport.request<any>('PUT', `${MEETINGS_BASE}/settings`, { body: settings }),
      );
    },
  };
}

// ---------------------------------------------------------------------------
// Errors (same shape as the calls API: {detail, code?})
// ---------------------------------------------------------------------------

/**
 * A meetings API error. Join errors (contract): 409 `meeting_not_open` (with
 * `joinable_from`), `meeting_cancelled`, `meeting_ended`, 404 `not_found`,
 * 503 `video_unavailable` — each with a plain en / es message in `detail`.
 */
export class MeetingsApiError extends CallsApiError {
  joinableFrom: string | null;
  constructor(message: string, status: number, code: string | null, callId: number | null, joinableFrom: string | null) {
    super(message, status, code, callId);
    this.name = 'MeetingsApiError';
    this.joinableFrom = joinableFrom;
  }
}

function errorFromBody(status: number, data: any, fallback: string): MeetingsApiError {
  const detail = data?.detail;
  const message =
    typeof detail === 'string' ? detail : typeof detail?.message === 'string' ? detail.message : fallback;
  const code = (typeof data?.code === 'string' && data.code) || (typeof detail?.code === 'string' && detail.code) || null;
  const rawCallId = Number(data?.call_id ?? detail?.call_id);
  const joinableFrom = str(data?.joinable_from) || str(detail?.joinable_from);
  return new MeetingsApiError(message, status, code, Number.isFinite(rawCallId) && rawCallId > 0 ? rawCallId : null, joinableFrom);
}

export const staffMeetingsTransport: MeetingsTransport = {
  async request(method, path, opts = {}) {
    try {
      const res = await apiClient.request({ method, url: path, params: opts.params, data: opts.body ?? (method === 'GET' || method === 'DELETE' ? undefined : {}) });
      return res.data;
    } catch (err: any) {
      throw errorFromBody(err?.response?.status ?? 0, err?.response?.data, err?.message || 'Request failed');
    }
  },
  async blob(path) {
    try {
      const res = await apiClient.get(path, { responseType: 'blob' });
      return res.data as Blob;
    } catch (err: any) {
      throw errorFromBody(err?.response?.status ?? 0, null, err?.message || 'Request failed');
    }
  },
};

export const memberMeetingsTransport: MeetingsTransport = {
  async request(method, path, opts = {}) {
    const qs = opts.params
      ? `?${new URLSearchParams(Object.entries(opts.params).map(([k, v]) => [k, String(v)])).toString()}`
      : '';
    const init: RequestInit = { method };
    if (method !== 'GET') init.body = JSON.stringify(opts.body ?? {});
    const res = await memberApiFetch(`${path}${qs}`, init);
    let data: any = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) throw errorFromBody(res.status, data, 'Request failed');
    return data;
  },
  async blob(path) {
    const res = await memberApiFetch(path);
    if (!res.ok) throw errorFromBody(res.status, null, 'Request failed');
    return res.blob();
  },
};

export const meetingsApi = createMeetingsApi(staffMeetingsTransport, 'staff');
export const memberMeetingsApi = createMeetingsApi(memberMeetingsTransport, 'member');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Where a meeting is in its life, for the card (and the Join button). */
export type MeetingPhase = 'upcoming' | 'joinable' | 'live' | 'ended' | 'cancelled';

export function meetingPhase(m: Pick<Meeting, 'status' | 'ends_at' | 'can_join_now' | 'joinable_from'>, now = Date.now()): MeetingPhase {
  if (m.status === 'cancelled') return 'cancelled';
  if (m.status === 'ended') return 'ended';
  if (m.status === 'live') return 'live';
  const end = parseCallTime(m.ends_at);
  if (end != null && now > end) return 'ended';
  if (m.can_join_now) return 'joinable';
  const from = parseCallTime(m.joinable_from);
  if (from != null && now >= from) return 'joinable';
  return 'upcoming';
}

/** The caller's own RSVP: the server's `my_rsvp`, else the attendee row matching their user / member id. */
export function myRsvpOf(m: Meeting, me: { userId?: number | null; memberId?: number | null }): MeetingRsvp | null {
  if (m.my_rsvp) return m.my_rsvp;
  const row = m.attendees.find(
    (a) => (me.userId != null && a.user_id === me.userId) || (me.memberId != null && a.member_id === me.memberId),
  );
  return row?.rsvp ?? null;
}

/** A loose but practical email check for the external-guest list. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/** The `?meeting=ID` a link points at, or null. */
export function meetingIdFromSearch(search: string): number | null {
  try {
    const raw = new URLSearchParams(search).get('meeting');
    const id = Number(raw);
    return raw && Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}
